import { OFFER_CODE_MIRRORING_NOTICE } from '@aysar/contracts';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { type FieldDef } from '@/shared/components/dynamic-form/types/dynamic-form.types';
import { useToast } from '@/shared/hooks/useToast/useToast';
import { toApiError } from '@/shared/utils/transform-error/transform-error';
import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

import {
  buildOfferCodeCreateFields,
  buildOfferCodeEditFields,
} from '../../config/offer-code-form.config';
import {
  OfferCodeCreateFormSchema,
  OfferCodeEditFormSchema,
  type OfferCodeCreateFormValues,
  type OfferCodeEditFormValues,
} from '../../config/offer-code-schema.config';
import {
  useCreateOfferCodeMutation,
  useOfferCodeQuery,
  useUpdateOfferCodeMutation,
} from '../../services/iap-offer-codes/iap-offer-codes.api';
import {
  offerCodeErrorCopy,
  type OfferCodeErrorCopy,
} from '../../utils/offer-code-error/offer-code-error.util';

/** The union the one dialog edits — six fields creating, three editing. */
export type OfferCodeFormValues = OfferCodeCreateFormValues | OfferCodeEditFormValues;

export interface UseOfferCodeModalLogicInput {
  /** The mapping being edited, or `null` to create one. */
  id: string | null;
  onDone: () => void;
}

export interface UseOfferCodeModalLogic {
  title: string;
  description: string;
  submitLabel: string;
  /** The mirroring limitation, translated — never the English contract constant. */
  mirroringNotice: string;
  mirroringNoticeTitle: string;
  fields: FieldDef[];
  schema: z.ZodType<OfferCodeFormValues, OfferCodeFormValues>;
  initialValues: OfferCodeFormValues;
  /** The row has not arrived yet. The form is NOT rendered while this is true. */
  isLoading: boolean;
  /** What the last refusal was, classified — `null` when unmapped or none. */
  refusal: OfferCodeErrorCopy | null;
  /** The sentence to draw, translated where this build knows the code. */
  refusalMessage: string | null;
  reload: () => void;
  submit: (values: OfferCodeFormValues) => Promise<void>;
}

/**
 * Mapping a discount rule to a store's offer code — the create and the edit.
 *
 * ## One dialog, two genuinely different forms
 *
 * `discountRuleId · productKey · store` are the mapping's identity and the
 * unique index over it, and `IapOfferCodeMappingUpdateSchema` omits all three:
 * re-pointing a code at another rule is a *different mapping*, not an edit. So
 * this is six fields creating and three editing, rather than one form with half
 * its controls greyed out — a control that looks answerable and is not is worse
 * than one that is absent.
 *
 * ## It re-reads by id rather than being handed the row
 *
 * `ModalData` is serializable-only, and that is the safer design anyway: a row
 * smuggled through the store would be a snapshot a colleague may have changed.
 * The form is withheld until it lands, because RHF reads `defaultValues` **once**
 * — a dialog mounted before the row opens empty and stays empty after it arrives.
 *
 * ## The notice is a WARNING, so it goes above the fields
 *
 * Apple and Google own these codes: mapping one here records what it stands for
 * and changes neither its existence nor its value. That has to be read *before*
 * the form is filled in, which is `DynamicFormModal`'s `banner` slot — the form's
 * own `children` land under the fields, which is right for a refusal and wrong
 * for a warning. `OFFER_CODE_MIRRORING_NOTICE` is the canonical English
 * statement of the fact; what an operator reads is the catalogs' translation of
 * it (U8), and the constant is quoted rather than re-invented.
 *
 * ## A refusal goes on the control that earned it
 *
 * `billing_offer_code_mapping_conflict` carries `details.offerCode`, which reads
 * as though the *code* were the duplicate. It is not — the unique key is
 * (rule, product, store) — so the client re-places it onto those three controls
 * (P21b). An unmapped code is re-thrown untouched, so the server's own sentence
 * still shows.
 */
export function useOfferCodeModalLogic({
  id,
  onDone,
}: UseOfferCodeModalLogicInput): UseOfferCodeModalLogic {
  const { t } = useTranslation('subscriptions');
  const toast = useToast();

  const isEditing = id !== null;

  // Nothing to read is not an empty request — it is no request.
  const query = useOfferCodeQuery(id ?? '', { skip: !isEditing });
  const [createOfferCode] = useCreateOfferCodeMutation();
  const [updateOfferCode] = useUpdateOfferCodeMutation();

  const [refusalError, setRefusalError] = useState<ApiError | null>(null);

  const row = query.data;

  const fields = useMemo(
    () => (isEditing ? buildOfferCodeEditFields(t) : buildOfferCodeCreateFields(t)),
    [isEditing, t],
  );

  const initialValues = useMemo<OfferCodeFormValues>(() => {
    if (isEditing) {
      return {
        offerCode: row?.offerCode ?? '',
        isActive: row?.isActive ?? true,
        note: row?.note ?? null,
      };
    }

    return {
      discountRuleId: '',
      productKey: '',
      // The larger of the two stores, and a value rather than an empty select:
      // `store` is required and a placeholder that is not a choice is a control
      // the operator has to answer twice.
      store: 'app_store',
      offerCode: '',
      // A code somebody has taken the trouble to map is a code meant to be used.
      isActive: true,
      note: null,
    };
  }, [isEditing, row]);

  const submit = useCallback(
    async (values: OfferCodeFormValues) => {
      setRefusalError(null);

      try {
        if (isEditing && id !== null) {
          await updateOfferCode({ id, body: values as OfferCodeEditFormValues }).unwrap();
        } else {
          await createOfferCode(values as OfferCodeCreateFormValues).unwrap();
        }

        toast.success(t(isEditing ? 'offerCodes.done.saved' : 'offerCodes.done.mapped'));
        onDone();
      } catch (reason) {
        const error = toApiError(reason);
        setRefusalError(error);
        throw placeRefusal(error, t);
      }
    },
    [isEditing, id, updateOfferCode, createOfferCode, toast, t, onDone],
  );

  const refusal = offerCodeErrorCopy(refusalError);

  return {
    title: t(isEditing ? 'offerCodes.form.editTitle' : 'offerCodes.form.createTitle'),
    description: t(
      isEditing ? 'offerCodes.form.editDescription' : 'offerCodes.form.createDescription',
    ),
    submitLabel: t(isEditing ? 'offerCodes.form.submitEdit' : 'offerCodes.form.submitCreate'),
    // The translation is what an operator reads (U8). The contract's constant is
    // the FALLBACK: a missing key would otherwise render the key's own name, and
    // a warning that degrades to `offerCodes.mirroringNotice` is a warning
    // nobody heeds.
    mirroringNotice: t('offerCodes.mirroringNotice', { defaultValue: OFFER_CODE_MIRRORING_NOTICE }),
    mirroringNoticeTitle: t('offerCodes.mirroringNoticeTitle'),
    fields,
    schema: (isEditing ? OfferCodeEditFormSchema : OfferCodeCreateFormSchema) as z.ZodType<
      OfferCodeFormValues,
      OfferCodeFormValues
    >,
    initialValues,
    // Only the EDIT waits: a create has no row to read, so withholding it there
    // would be a skeleton over a form that was ready.
    isLoading: isEditing && query.isLoading,
    refusal,
    refusalMessage: messageFor(refusalError, refusal, t),
    reload: () => {
      void query.refetch();
    },
    submit,
  };
}

/** The module's translate function, narrowed the way `Translate` is elsewhere. */
type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * The sentence to draw for a refusal.
 *
 * This build's own copy where it knows the code, and **the api's own message**
 * where it does not — a code from a newer api must not be swallowed into a
 * generic key the server already wrote a localized sentence for.
 */
function messageFor(
  error: ApiError | null,
  copy: OfferCodeErrorCopy | null,
  t: Translate,
): string | null {
  if (error === null) {
    return null;
  }

  return copy === null ? error.message : t(copy.i18nKey);
}

/**
 * Puts a refusal on the controls that earned it, when the api could not.
 *
 * The conflict's own `details.offerCode` names the wrong field — the duplicate
 * is the (rule, product, store) key — and `DiscountRuleNotFoundError` names a
 * field the form has, so both are re-placed. `mapServerErrors` then pins each
 * sentence to its control (W9).
 */
function placeRefusal(error: ApiError, t: Translate): ApiError {
  const copy = offerCodeErrorCopy(error);

  if (copy === null || copy.fields.length === 0) {
    // Untouched, so the server's own sentence still shows (P21b).
    return error;
  }

  const sentence = t(copy.i18nKey);

  return {
    ...error,
    details: {
      ...error.details,
      ...Object.fromEntries(copy.fields.map((field) => [field, [sentence]])),
    },
  };
}
